// AGENT-BB: MetricsTable — flat PBI-pivot renderer.
//
// This is a full rewrite of the previous Category × FY grid component. The
// dashboard now mirrors the actual PBI pivot visual (default
// "4c898cd2302a19296e3b" — "Last 12 Months"). The backend ships the pivot
// already shaped (columns + flat rows) via `_evaluate_visual`, so this
// component does no aggregation — it just renders.
//
// Responsibilities:
//   1. Read `reportData.pivot.{columns, rows, title}` from the shared
//      useReportData hook (queryKey: ["report", filterKey] — shared with BC).
//   2. Auto-detect "row header" columns (the leading consecutive string
//      columns) and make them sticky-left.
//   3. Auto-format cells by column-name pattern + dtype: currency, percent,
//      integer count, date, or plain string.
//   4. Click-to-sort each <th> with tri-state (idle → desc → asc → idle).
//   5. Virtualize the body via @tanstack/react-virtual when rows > 200.
//   6. Empty state with a "Reset filters" link.
//
// No FY group-header logic, no metric-key adapter, no broadcast-bug detector.
// The new wire shape makes that whole class of bug impossible.

import type { CSSProperties } from "react";
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useShallow } from "zustand/react/shallow";
import { useFilterStore } from "../state/filterStore";
import { useUiStore } from "../state/uiStore";
import { postReport } from "../api/client";
import type {
  PivotCellValue,
  PivotColumn,
  PivotRow,
  ReportResponse,
  ServerFilterShape,
} from "../types";
import {
  formatDateMMDDYYYY,
  formatYear,
  humanizeColumnName,
  isDateLike,
  isYearColumn,
} from "../format";
import "./MetricsTable.css";
import "./MetricsTable.condfmt.css";

/* ------------------------------------------------------------------------- *
 * Constants.
 * ------------------------------------------------------------------------- */

const VIRTUALIZE_THRESHOLD = 200;
const ROW_HEIGHT_PX = 32;

/**
 * AGENT-DASH-PARITY: secondary table view.
 *
 * PBI's HSQ page ships a prominent "by Parent Product" tableEx (visual
 * `0e4039130424e90e0345`) alongside the main per-product pivot. The
 * rebuild was missing this view entirely — adding it as a tab strip on
 * the MetricsTable card gives the user a 1:1 visual to the PBI page.
 *
 * The default tab renders whatever pivot is currently selected by the
 * ViewSelectors (FY / YoY / QoQ — driven by the store's `pivotVisualId`).
 * The "by Parent Product" tab locally overrides that id so the table
 * card can flip between scopes without disturbing the charts (which
 * still mirror their own visual ids from the same /report response).
 */
const PARENT_PRODUCT_TABLE_VID = "0e4039130424e90e0345";

type TableScope = "default" | "parent";

/**
 * AGENT-DASH-PARITY: column-name patterns that identify the "mode"
 * (quotes-vs-orders) emphasis columns. PBI's HSQ bookmarks toggle which
 * set of columns is highlighted — Quotes mode emphasises Issued/Quoted,
 * Orders mode emphasises Converted/Sold/Won. The rebuild now visually
 * bolds the matching columns when `filterStore.mode` flips.
 */
const RX_QUOTES_COLS = /(QUOTES_?ISSUED|IA_QuotesIssued|IA_QtyQuoted|RQ)$/i;
const RX_ORDERS_COLS = /(QUOTES_?CONVERTED|QUOTES_?WON|IA_QuotesConverted|IA_QuotesWon|IA_QtySold|ORDERS|RP)$/i;

/** Numeric dtypes emitted by `_evaluate_visual`.
 *
 * AGENT-PV13: backend emits pandas dtypes — `Int64`, `float64`, `int64`,
 * `int32`, etc. The original set held only Python-name strings and
 * silently failed the `(col.dtype || "").toLowerCase()` check, so EVERY
 * numeric column was treated as non-numeric — no currency / percent /
 * count formatting and string-sort instead of numeric-sort. Add the
 * pandas variants explicitly.
 */
const NUMERIC_DTYPES = new Set([
  "int", "integer", "float", "double", "number", "decimal",
  "int8", "int16", "int32", "int64",
  "uint8", "uint16", "uint32", "uint64",
  "float16", "float32", "float64",
  "Int64", "Float64",
]);

/* ------------------------------------------------------------------------- *
 * Pattern detectors for column-name heuristics.
 * ------------------------------------------------------------------------- */

const RX_CURRENCY = /(SELL|PRICE|REVENUE|MAXDISCOUNT|TUI_)/i;
const RX_PERCENT = /(RATE|PERC|PCT|RATIO|CONVERSION|CloseRate|WonRate|ConversionRate|Won_?Percent|Won_?%)/i;
// YoY / QoQ change columns: their raw names carry "Revenue" (→ would format as
// currency) but the values are fractional period-over-period changes
// (0.97 = +97%). Detected separately so they render as signed percentages.
const RX_YOY_QOQ = /(YoY|QoQ)/i;
const RX_DATE = /DATE|LASTQUOTED|LASTSOLD/i;
const RX_INT_COUNT = /QUOTES_?ISSUED|QUOTES_?CONVERTED|QTY_?|RP|RQ|ORDERS/i;

function isNumericDType(dtype: string): boolean {
  const raw = dtype || "";
  return NUMERIC_DTYPES.has(raw) || NUMERIC_DTYPES.has(raw.toLowerCase());
}

/* ------------------------------------------------------------------------- *
 * AGENT-B6: conditional-formatting classifier + per-column stats.
 *
 * Two visual treatments mirror PBI's HSQ pivot:
 *   - DATA-BAR: horizontal blue gradient whose length is proportional to
 *     value / column_max. Applied to currency / revenue / count / qty
 *     columns. Negative-capable columns (YoY / delta) grow from the
 *     50%-center axis (positive → right, negative → left).
 *   - HEATMAP: 3-stop red → yellow → green interpolation across the
 *     column's [min, max], applied as background-color. Reserved for
 *     percent / rate / ratio columns.
 *
 * Classification is by display-label patterns (post-humanize) so the
 * user-visible column names drive the decision — easier to reason
 * about than the raw DAX tokens. Dtype is the tiebreaker: only
 * numeric dtypes ever decorate.
 * ------------------------------------------------------------------------- */

type CondFmtKind = "databar" | "heatmap" | "yoyqoq" | "none";

// Display-label patterns. `displayName` is the humanized label, e.g.
// "$ Revenue", "Quotes Issued", "Conversion Rate".
const RX_DATABAR_LABEL = /(\$|Revenue|Total|Issued|Won|Converted|Qty|Quoted|Sold|Orders|Count)/i;
const RX_HEATMAP_LABEL = /(%$|Rate|Ratio|Conversion|Margin %)/i;

// Negative-capable: YoY / change / delta / variance columns. These render
// the bar from a centered axis so the sign reads at a glance.
const RX_DATABAR_CENTERED = /(YoY|QoQ|Change|Delta|Variance|Growth|vs\.?\s|Diff)/i;

function classifyColumn(
  rawName: string,
  displayName: string,
  dtype: string,
): CondFmtKind {
  if (!isNumericDType(dtype)) return "none";
  // YoY / QoQ change columns get absolute traffic-light coloring (negative →
  // red, 0–100% → yellow, >100% → green) instead of the relative min/max
  // heatmap. Must precede the heatmap branch: their humanized labels end in
  // "%" ("Revenue YoY %") so they'd otherwise be swallowed by RX_HEATMAP_LABEL.
  if (RX_YOY_QOQ.test(rawName) || RX_YOY_QOQ.test(displayName)) {
    return "yoyqoq";
  }
  // Heatmap wins over data-bar — percent columns also match RX_DATABAR_LABEL
  // (no, they don't via the current regex, but be defensive).
  if (RX_HEATMAP_LABEL.test(displayName) || RX_PERCENT.test(rawName)) {
    return "heatmap";
  }
  if (RX_DATABAR_LABEL.test(displayName) || RX_CURRENCY.test(rawName) || RX_INT_COUNT.test(rawName)) {
    return "databar";
  }
  // Fall-through: any int/float column gets a data-bar. The rationale is
  // PBI's default is "always show a bar"; opting out is rare.
  const dt = (dtype || "").toLowerCase();
  if (dt === "int64" || dt === "float64" || dt === "int32" || dt === "float32") {
    return "databar";
  }
  return "none";
}

interface ColumnStats {
  min: number;
  max: number;
  absMax: number; // for centered/symmetric bars
  hasNegative: boolean;
}

function computeColumnStats(rows: PivotRow[], colName: string): ColumnStats {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let hasNegative = false;
  for (const row of rows) {
    const n = coerceNumber(row[colName] ?? null);
    if (n === null) continue;
    if (n < min) min = n;
    if (n > max) max = n;
    if (n < 0) hasNegative = true;
  }
  if (!Number.isFinite(min)) min = 0;
  if (!Number.isFinite(max)) max = 0;
  return {
    min,
    max,
    absMax: Math.max(Math.abs(min), Math.abs(max)),
    hasNegative,
  };
}

/** Lerp a hex color between two stops at t∈[0,1]. */
function lerpHex(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ar = (pa >> 16) & 0xff;
  const ag = (pa >> 8) & 0xff;
  const ab = pa & 0xff;
  const br = (pb >> 16) & 0xff;
  const bg = (pb >> 8) & 0xff;
  const bb = pb & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const b2 = Math.round(ab + (bb - ab) * t);
  return `rgb(${r}, ${g}, ${b2})`;
}

// 3-stop heatmap. Keep these in sync with the CSS vars in
// MetricsTable.condfmt.css (low/mid/high); duplicating them here so the
// component can interpolate without reading computed-style on every cell.
const HEATMAP_LOW = "#fee2e2";
const HEATMAP_MID = "#fef9c3";
const HEATMAP_HIGH = "#dcfce7";

// Dark-theme heatmap stops — muted red→amber→green that read on the dark
// table bg (#161a21/#1e232c) with light text. The light pastels above set the
// cell bg via INLINE style, so they didn't flip with the theme; pick these
// when dark so the rate/percent columns convert like the rest of the table.
const HEATMAP_LOW_DARK = "#5e2a2a";
const HEATMAP_MID_DARK = "#5b5226";
const HEATMAP_HIGH_DARK = "#27513a";

interface HeatStops {
  low: string;
  mid: string;
  high: string;
}

function heatmapColor(
  value: number,
  min: number,
  max: number,
  stops: HeatStops,
): string {
  if (!Number.isFinite(value) || max <= min) return stops.mid;
  const t = Math.min(1, Math.max(0, (value - min) / (max - min)));
  if (t <= 0.5) return lerpHex(stops.low, stops.mid, t / 0.5);
  return lerpHex(stops.mid, stops.high, (t - 0.5) / 0.5);
}

// Absolute traffic-light color for YoY / QoQ change columns. `value` is the
// fractional period-over-period change (0.97 = +97%, -0.65 = −65%). Unlike the
// relative heatmap, the thresholds are fixed so the same growth always reads
// the same color across products and views:
//   value < 0   → red    (any decline)
//   0 ≤ value ≤ 1 → yellow (0–100% growth)
//   value > 1   → green  (>100% growth)
// Reuses the heatmap's low/mid/high stops (red/yellow/green) so the palette
// stays consistent with the rate columns under both themes.
function yoyQoqColor(value: number, stops: HeatStops): string {
  if (!Number.isFinite(value)) return stops.mid;
  if (value < 0) return stops.low;
  if (value <= 1) return stops.mid;
  return stops.high;
}

/* ------------------------------------------------------------------------- *
 * Row-header detection — the first consecutive string columns are the
 * "row headers" (Parent Product / Description / Part #). Everything after
 * the first non-string column is a measure.
 * ------------------------------------------------------------------------- */

function reorderColumns(cols: PivotColumn[]): PivotColumn[] {
  const idxQuotesWon = cols.findIndex(c =>
    c.name === "Item Analysis.IA_QuotesWon_Dynamic" ||
    c.name === "IA_QuotesWon_Dynamic" ||
    c.name === "IA_QuotesWon" ||
    c.name === "Item Analysis.IA_QuotesWon"
  );
  const idxOrders = cols.findIndex(c =>
    c.name === "Item Analysis.IA_OrdersReleased_Dynamic" ||
    c.name === "IA_OrdersReleased_Dynamic"
  );
  if (idxQuotesWon < 0 || idxOrders < 0 || idxOrders === idxQuotesWon + 1) {
    return cols;
  }
  const next = cols.slice();
  const [removed] = next.splice(idxOrders, 1);
  const insertAt = idxOrders < idxQuotesWon ? idxQuotesWon : idxQuotesWon + 1;
  next.splice(insertAt, 0, removed);
  return next;
}

function countRowHeaderColumns(columns: PivotColumn[]): number {
  let n = 0;
  for (const col of columns) {
    // AGENT-PV13: backend emits dtype="str" (pandas StringDtype short name),
    // not "string". Accept both so PRODUCT_NAME / PART_NUMBER get sticky-left.
    const dt = (col.dtype || "").toLowerCase();
    if (dt === "string" || dt === "str" || dt === "object") n++;
    else break;
  }
  // If somehow every column is a string (degenerate pivot), keep just the
  // first one sticky — the rest get normal positioning.
  if (n === columns.length && n > 1) return 1;
  return n;
}

/* ------------------------------------------------------------------------- *
 * Formatting.
 * ------------------------------------------------------------------------- */

const FMT_CURRENCY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const FMT_INT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const FMT_PCT_1 = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function coerceNumber(v: PivotCellValue): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const stripped = v.replace(/[,$%\s]/g, "");
    if (stripped === "") return null;
    const n = Number(stripped);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatDate(value: PivotCellValue): string {
  if (value === null || value === undefined || value === "") return "—";
  // ISO-style or parseable string/number/date.
  const d = new Date(value as string | number);
  if (Number.isNaN(d.getTime())) return String(value);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
}

function formatValue(
  columnName: string,
  value: PivotCellValue,
  dtype: string,
): string {
  // Universal null guard.
  if (value === null || value === undefined) return "—";

  const numeric = isNumericDType(dtype);

  // Date-like (column-name heuristic — independent of dtype because some
  // backends emit ISO date strings with dtype="string").
  if (RX_DATE.test(columnName)) {
    return formatDate(value);
  }

  // YoY / QoQ change — fractional change rendered as a signed percent. Must
  // precede the currency branch (the raw name contains "Revenue").
  if (numeric && RX_YOY_QOQ.test(columnName)) {
    const n = coerceNumber(value);
    if (n === null) return "—";
    const pct = n * 100;
    return `${pct >= 0 ? "+" : ""}${FMT_PCT_1.format(pct)}%`;
  }

  // Currency-like — numeric only.
  if (numeric && RX_CURRENCY.test(columnName)) {
    const n = coerceNumber(value);
    if (n === null) return "—";
    return FMT_CURRENCY.format(n);
  }

  // Percent-like — numeric only. Values > 1 are assumed to already be in
  // percent points (e.g. 42.5 -> "42.5%"); values ≤ 1 are treated as fractions
  // (e.g. 0.425 -> "42.5%").
  if (numeric && RX_PERCENT.test(columnName)) {
    const n = coerceNumber(value);
    if (n === null) return "—";
    const pct = n > 1 ? n : n * 100;
    return `${FMT_PCT_1.format(pct)}%`;
  }

  // Integer count — numeric only.
  if (numeric && RX_INT_COUNT.test(columnName)) {
    const n = coerceNumber(value);
    if (n === null) return "—";
    return FMT_INT.format(n);
  }

  // Generic numeric — show with thousands separators, no decimals over 1000.
  if (numeric) {
    const n = coerceNumber(value);
    if (n === null) return "—";
    if (Math.abs(n) >= 1000) return FMT_INT.format(n);
    // Below 1000, allow up to 2 decimals.
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);
  }

  // Strings / booleans / other — fall back to String() with empty-string ->
  // em-dash.
  const s = String(value);
  return s === "" ? "—" : s;
}

/* ------------------------------------------------------------------------- *
 * Header tooltips — only for columns whose name doesn't already explain the
 * measure. Self-evident columns return null and keep the plain "Sort by …"
 * affordance hint, so we don't add noise where the label is obvious.
 * ------------------------------------------------------------------------- */

function columnHint(name: string): string | null {
  if (/QoQ/i.test(name)) return "Revenue change vs the prior calendar quarter";
  if (/YoY/i.test(name)) return "Revenue change vs the prior calendar year";
  if (/Conversion/i.test(name)) return "Quotes Won ÷ Quotes Issued";
  if (/Unreleased/i.test(name)) return "Ordered, not yet shipped / revenue-recognized";
  if (/Calculated Total/i.test(name))
    return "Revenue — released original orders: qty × (sell − discount)";
  return null;
}

function headerTitle(name: string, displayName: string): string {
  const hint = columnHint(name);
  return hint ? `${hint} · click to sort` : `Sort by ${displayName}`;
}

/* ------------------------------------------------------------------------- *
 * Sorting — tri-state (idle → desc → asc → idle) per column.
 * ------------------------------------------------------------------------- */

interface SortKey {
  column: string;
  dir: "asc" | "desc";
}

function compareCells(
  a: PivotCellValue,
  b: PivotCellValue,
  dtype: string,
): number {
  // Nulls always sort to the bottom (regardless of dir).
  const aNull = a === null || a === undefined || a === "";
  const bNull = b === null || b === undefined || b === "";
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  if (isNumericDType(dtype)) {
    const an = coerceNumber(a) ?? Number.NEGATIVE_INFINITY;
    const bn = coerceNumber(b) ?? Number.NEGATIVE_INFINITY;
    return an - bn;
  }

  // Date columns sort as dates if parseable.
  const aStr = String(a);
  const bStr = String(b);
  const ad = Date.parse(aStr);
  const bd = Date.parse(bStr);
  if (!Number.isNaN(ad) && !Number.isNaN(bd)) return ad - bd;

  // String compare (case-insensitive natural-ish).
  return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: "base" });
}

function applySort(
  rows: PivotRow[],
  sort: SortKey | null,
  columnDType: string,
): PivotRow[] {
  if (!sort) return rows;
  const sorted = rows.slice();
  sorted.sort((a, b) => {
    const c = compareCells(a[sort.column], b[sort.column], columnDType);
    return sort.dir === "asc" ? c : -c;
  });
  return sorted;
}

/* ------------------------------------------------------------------------- *
 * AGENT-DASH-TREE: group the multi-period pivot (YoY / QoQ) by its parent
 * dimension so the table collapses to ONE row per Parent Product, expandable
 * (like the category sidebar) to reveal the per-year / per-quarter detail.
 *
 * The multi-period pivots ship one flat row per (period × product) — ~376
 * rows the user has to scroll through. Folding the leading period column into
 * a tree under each Parent Product cuts the default view to ~94 rows and lets
 * the user drill into a product's individual years on demand.
 *
 * Single-grain pivots (FY "Last 12 Months", which has one row per product and
 * no leading period column) don't satisfy the grouping precondition and fall
 * straight through to the existing flat renderer.
 * ------------------------------------------------------------------------- */

function isStringDType(dtype: string): boolean {
  const dt = (dtype || "").toLowerCase();
  return dt === "string" || dt === "str" || dt === "object";
}

interface GroupNode {
  name: string;
  children: PivotRow[]; // sorted ascending by the folded dimension cols
  agg: PivotRow; // synthetic summary row (sums; blended conversion rate)
}

interface GroupConfig {
  groupCol: PivotColumn; // the dimension we group by (e.g. Parent Product)
  childCols: PivotColumn[]; // leading dims folded into the tree (e.g. Year)
  measureCols: PivotColumn[]; // everything after the group col
  groups: GroupNode[];
  aggRows: PivotRow[]; // parallel to groups — feeds conditional-fmt stats
}

/** A row in the grouped render list: a parent (group) row or one of its
 *  expanded child rows. */
type DisplayRow =
  | { kind: "group"; node: GroupNode }
  | { kind: "child"; row: PivotRow };

function sumColumn(rows: PivotRow[], name: string): number | null {
  let sum = 0;
  let any = false;
  for (const r of rows) {
    const n = coerceNumber(r[name] ?? null);
    if (n !== null) {
      sum += n;
      any = true;
    }
  }
  return any ? sum : null;
}

/** Build a parent summary row: additive measures sum; percent / YoY-delta
 *  columns stay blank (a sum of rates is meaningless); the conversion-rate
 *  column is recomputed from summed Won / Issued when both are present; and a
 *  Revenue-YoY column is recomputed as the change between the two most recent
 *  COMPLETE calendar years (so the collapsed row shows recent annual growth
 *  rather than a meaningless roll-up). */
function buildAggRow(
  children: PivotRow[],
  measureCols: PivotColumn[],
  childCols: PivotColumn[],
): PivotRow {
  const agg: PivotRow = {};
  for (const col of measureCols) {
    const display = humanizeColumnName(col.name);
    const isPct = RX_PERCENT.test(col.name) || RX_HEATMAP_LABEL.test(display);
    const isCentered =
      RX_DATABAR_CENTERED.test(display) || RX_DATABAR_CENTERED.test(col.name);
    if (isPct || isCentered) {
      agg[col.name] = null;
    } else if (isNumericDType(col.dtype)) {
      agg[col.name] = sumColumn(children, col.name);
    } else {
      agg[col.name] = null;
    }
  }
  const convCol = measureCols.find((c) => /Conversion/i.test(c.name));
  const issuedCol = measureCols.find((c) => /QuotesIssued/i.test(c.name));
  const wonCol = measureCols.find((c) => /QuotesWon|QuotesConverted/i.test(c.name));
  if (convCol && issuedCol && wonCol) {
    const iss = sumColumn(children, issuedCol.name) ?? 0;
    const won = sumColumn(children, wonCol.name) ?? 0;
    agg[convCol.name] = iss > 0 ? won / iss : null;
  }

  // Revenue YoY% on the parent = growth between the latest two COMPLETE
  // calendar years (the current year is partial, so it's excluded).
  const yoyCol = measureCols.find((c) => /YoY/i.test(c.name));
  const revCol = measureCols.find(
    (c) => RX_CURRENCY.test(c.name) && !RX_YOY_QOQ.test(c.name),
  );
  const yearCol = childCols.find((c) =>
    isYearColumn(c.name, humanizeColumnName(c.name)),
  );
  if (yoyCol && revCol && yearCol) {
    const curYear = new Date().getFullYear();
    const revByYear = new Map<number, number>();
    for (const r of children) {
      const y = coerceNumber(r[yearCol.name] ?? null);
      const rev = coerceNumber(r[revCol.name] ?? null);
      if (y === null || rev === null) continue;
      revByYear.set(y, (revByYear.get(y) ?? 0) + rev);
    }
    const years = [...revByYear.keys()]
      .filter((y) => y < curYear)
      .sort((a, b) => a - b);
    if (years.length >= 2) {
      const latest = revByYear.get(years[years.length - 1])!;
      const prior = revByYear.get(years[years.length - 2])!;
      agg[yoyCol.name] = prior !== 0 ? (latest - prior) / prior : null;
    }
  }
  return agg;
}

function compareByChildCols(
  a: PivotRow,
  b: PivotRow,
  childCols: PivotColumn[],
): number {
  for (const c of childCols) {
    const cmp = compareCells(a[c.name], b[c.name], c.dtype);
    if (cmp !== 0) return cmp;
  }
  return 0;
}

/* ------------------------------------------------------------------------- *
 * Component.
 * ------------------------------------------------------------------------- */

export function MetricsTable(): JSX.Element {
  // AGENT-PV8: subscribe to every field that `toServerShape()` reads, not
  // just the function reference. The previous selector
  // `useFilterStore((s) => s.toServerShape)` returned a stable function
  // ref so Zustand never re-rendered the component on filter mutations.
  // Category clicks toggled the Set but the component kept calling
  // `toServerShape()` against the same render — the queryKey was stuck
  // on the initial empty filter shape, react-query never refetched, and
  // the filter appeared to "do nothing". `useShallow` checks each field
  // ref so any toggle (which creates a new Set) triggers a re-render.
  const filterState = useFilterStore(
    useShallow((s) => ({
      categories: s.categories,
      parentProducts: s.parentProducts,
      partNumbers: s.partNumbers,
      vendors: s.vendors,
      booleans: s.booleans,
      // online tri-state lives outside `booleans` — without subscribing to
      // it here the wire shape never changes when Online Order is toggled
      // and react-query never refetches. See useReportData for context.
      online: s.online,
      newOrExisting: s.newOrExisting,
      mode: s.mode,
      dateRange: s.dateRange,
      viewLeft: s.viewLeft,
      viewRight: s.viewRight,
      pivotVisualId: s.pivotVisualId,
    })),
  );
  const toServerShape = useFilterStore((s) => s.toServerShape);
  const reset = useFilterStore((s) => s.reset);
  const mode = useFilterStore((s) => s.mode);
  // AGENT-DARKHEAT (2026-05-28): heatmap (rate/percent) cells set their bg
  // inline, so they never flipped with the theme — they stayed light pastel in
  // dark mode. Pick a dark red→amber→green palette + light text when the theme
  // is dark so those rightmost rate columns convert like the rest of the table.
  const theme = useUiStore((s) => s.theme);
  const heatStops: HeatStops =
    theme === "dark"
      ? { low: HEATMAP_LOW_DARK, mid: HEATMAP_MID_DARK, high: HEATMAP_HIGH_DARK }
      : { low: HEATMAP_LOW, mid: HEATMAP_MID, high: HEATMAP_HIGH };
  const heatTextColor = theme === "dark" ? "#eceef2" : "#1a2230";

  // AGENT-DASH-PARITY: secondary tab — "by Parent Product" (PBI visual
  // 0e4039130424e90e0345). Local state so flipping the tab doesn't
  // disturb the global pivotVisualId that ViewSelectors drives for the
  // FY/YoY/QoQ swap. When `scope === "parent"` we POST `/report` with
  // pivotVisualId overridden to the Parent Product tableEx.
  // Legacy tab strip hidden — `scope` is locked to "default" so the
  // table always shows the per-product pivot. To re-enable, replace
  // `{false && (` with `{(` around the bb-mt-tabs block below and
  // restore the `setScope` callbacks on the tab buttons.
  const [scope, setScope] = useState<TableScope>("default");

  const baseShape: ServerFilterShape = useMemo(
    () => toServerShape(),
    [filterState, toServerShape],
  );
  const filterShape: ServerFilterShape = useMemo(() => {
    if (scope === "parent") {
      return { ...baseShape, pivotVisualId: PARENT_PRODUCT_TABLE_VID };
    }
    return baseShape;
  }, [baseShape, scope]);
  const filterKey = useMemo(() => JSON.stringify(filterShape), [filterShape]);

  // Shared queryKey with charts/useReportData so react-query dedupes the
  // /api/dashboard/report POST across the table and both combo charts.
  // Without this, each consumer pays the full cold-cache cost separately.
  const query = useQuery<ReportResponse>({
    queryKey: ["dashboard-report", filterKey],
    queryFn: ({ signal }) => postReport(filterShape, signal),
    staleTime: 10_000,
  });

  const pivot = query.data?.pivot;
  const rawColumns: PivotColumn[] = pivot?.columns ?? [];
  const columns: PivotColumn[] = useMemo(() => reorderColumns(rawColumns), [rawColumns]);
  const rows: PivotRow[] = pivot?.rows ?? [];
  const title = pivot?.title ?? "Metrics";

  const rowHeaderCount = useMemo(() => countRowHeaderColumns(columns), [columns]);

  // AGENT-DASH-TREE: detect whether this pivot can be grouped into a
  // collapsible Parent-Product tree (multi-period pivots only). Returns null
  // for single-grain pivots, which keep the flat renderer.
  const groupCfg = useMemo<GroupConfig | null>(() => {
    // Prefer the Parent-Product dimension explicitly; fall back to the first
    // string column. This keeps the tree grouped by product even when a pivot
    // (e.g. QoQ) carries an extra period dimension before it.
    const RX_GROUP_COL = /(PP_Display|Parent.?Product|PRODUCT_NAME|Description)/i;
    let groupIdx = columns.findIndex((c) => RX_GROUP_COL.test(c.name));
    if (groupIdx < 0) groupIdx = columns.findIndex((c) => isStringDType(c.dtype));
    if (groupIdx < 0) return null;
    const groupCol = columns[groupIdx];
    const childCols = columns.slice(0, groupIdx); // leading period dims
    if (childCols.length === 0) return null; // nothing to nest underneath
    const measureCols = columns.slice(groupIdx + 1);
    const map = new Map<string, PivotRow[]>();
    for (const r of rows) {
      const k = String(r[groupCol.name] ?? "—");
      let arr = map.get(k);
      if (!arr) {
        arr = [];
        map.set(k, arr);
      }
      arr.push(r);
    }
    let maxSize = 0;
    for (const arr of map.values()) maxSize = Math.max(maxSize, arr.length);
    if (maxSize <= 1) return null; // single row per group → no benefit
    const groups: GroupNode[] = [];
    const aggRows: PivotRow[] = [];
    for (const [name, kids] of map) {
      const sorted = kids
        .slice()
        .sort((a, b) => compareByChildCols(a, b, childCols));
      const agg = buildAggRow(sorted, measureCols, childCols);
      groups.push({ name, children: sorted, agg });
      aggRows.push(agg);
    }
    return { groupCol, childCols, measureCols, groups, aggRows };
  }, [columns, rows]);

  // AGENT-DASH-TREE: which Parent-Product groups are expanded. Collapsed by
  // default for a compact first paint; local state survives re-renders (the
  // table never unmounts) the same way CategoryItem tracks its parents.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleGroup = (name: string): void => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const allExpanded =
    !!groupCfg &&
    groupCfg.groups.length > 0 &&
    groupCfg.groups.every((g) => expandedGroups.has(g.name));
  const setAllExpanded = (expand: boolean): void => {
    setExpandedGroups(
      expand && groupCfg
        ? new Set(groupCfg.groups.map((g) => g.name))
        : new Set(),
    );
  };

  // AGENT-B6: per-column classifier + min/max stats for conditional
  // formatting. Memoized on (columns, rows) — recomputed only when the
  // pivot payload changes, NOT on every scroll / sort tick. Sort
  // doesn't shift the value-set, so the stats stay valid.
  //
  // AGENT-DASH-TREE: in grouped mode the parent summary rows hold the column
  // maxima (they're sums), so the data-bar / heatmap stats must include the
  // aggregate rows — otherwise parent bars overflow 100%.
  const condFmtRows = groupCfg ? rows.concat(groupCfg.aggRows) : rows;
  const colCondFmt = useMemo(() => {
    const out = new Map<
      string,
      { kind: CondFmtKind; centered: boolean; stats: ColumnStats }
    >();
    for (const col of columns) {
      const displayName = humanizeColumnName(col.name);
      const kind = classifyColumn(col.name, displayName, col.dtype);
      if (kind === "none") {
        out.set(col.name, {
          kind,
          centered: false,
          stats: { min: 0, max: 0, absMax: 0, hasNegative: false },
        });
        continue;
      }
      const stats = computeColumnStats(condFmtRows, col.name);
      const centered =
        kind === "databar" &&
        (stats.hasNegative || RX_DATABAR_CENTERED.test(displayName));
      out.set(col.name, { kind, centered, stats });
    }
    return out;
  }, [columns, condFmtRows]);

  const [sort, setSort] = useState<SortKey | null>(null);
  const onHeaderClick = (col: PivotColumn): void => {
    setSort((prev) => {
      if (!prev || prev.column !== col.name) {
        return { column: col.name, dir: "desc" };
      }
      if (prev.dir === "desc") return { column: col.name, dir: "asc" };
      // asc -> idle
      return null;
    });
  };

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.name === sort.column);
    return applySort(rows, sort, col?.dtype ?? "string");
  }, [rows, sort, columns]);

  // AGENT-DASH-TREE: the flat list actually rendered when grouping is active —
  // an ordered run of group (parent) rows, each optionally followed by its
  // expanded child rows. Sorting a measure header reorders the GROUPS by their
  // aggregate; the group column sorts groups by name; children always stay in
  // chronological (period) order for readability.
  const displayRows = useMemo<DisplayRow[]>(() => {
    if (!groupCfg) return [];
    const ordered = groupCfg.groups.slice();
    if (sort) {
      if (sort.column === groupCfg.groupCol.name) {
        ordered.sort((a, b) => {
          const c = a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          });
          return sort.dir === "asc" ? c : -c;
        });
      } else {
        const col = groupCfg.measureCols.find((c) => c.name === sort.column);
        if (col) {
          ordered.sort((a, b) => {
            const c = compareCells(a.agg[sort.column], b.agg[sort.column], col.dtype);
            return sort.dir === "asc" ? c : -c;
          });
        }
      }
    }
    const out: DisplayRow[] = [];
    for (const node of ordered) {
      out.push({ kind: "group", node });
      if (expandedGroups.has(node.name)) {
        for (const child of node.children) out.push({ kind: "child", row: child });
      }
    }
    return out;
  }, [groupCfg, sort, expandedGroups]);

  const activeRowCount = groupCfg ? displayRows.length : sortedRows.length;
  const shouldVirtualize = activeRowCount > VIRTUALIZE_THRESHOLD;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: activeRowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT_PX,
    overscan: 8,
    enabled: shouldVirtualize,
  });

  /* ── Header chrome — tabs (scope) + mode pill (quotes/orders) ───────── */
  // AGENT-DASH-PARITY: extracted so loading/error/empty branches share it
  // — every visible state shows the same scope tabs + mode toggle, which
  // matches PBI's HSQ page where the bookmark navigators live at the top
  // of the page and stay visible regardless of the table contents.

  const tableChrome = (
    <>
      {/* Legacy "by Product / by Parent Group" tab strip hidden —
          replace `{false && (` with `{(` to re-enable */}
      {false && (
        <div className="bb-mt-tabs" role="tablist" aria-label="Table scope">
          <button
            type="button"
            role="tab"
            aria-selected={scope === "default"}
            className={`bb-mt-tab${scope === "default" ? " bb-mt-tab--selected" : ""}`}
            onClick={() => setScope("default")}
          >
            by Product
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={scope === "parent"}
            className={`bb-mt-tab${scope === "parent" ? " bb-mt-tab--selected" : ""}`}
            onClick={() => setScope("parent")}
            title="HSQ visual 0e4039130424e90e0345"
          >
            by Parent Group
          </button>
        </div>
      )}
      <div className="bb-mt-card-head">
        <h3 className="dash-h3">{title}</h3>
        {groupCfg ? (
          <button
            type="button"
            className="bb-mt-expand-all"
            onClick={() => setAllExpanded(!allExpanded)}
            title={allExpanded ? "Collapse every group" : "Expand every group"}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        ) : null}
      </div>
    </>
  );

  /* ── Branches: loading / error / empty ─────────────────────────────── */

  if (query.isLoading) {
    return (
      <div className="dash-card bb-mt-card">
        {tableChrome}
        <div className="bb-mt-skeleton" aria-busy="true" aria-label="Loading metrics">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="bb-mt-skeleton-row" key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="dash-card bb-mt-card">
        {tableChrome}
        <div className="bb-mt-empty">
          <span className="bb-mt-empty-text">
            Failed to load metrics. {(query.error as Error)?.message ?? ""}
          </span>
        </div>
      </div>
    );
  }

  if (columns.length === 0 || sortedRows.length === 0) {
    return (
      <div className="dash-card bb-mt-card">
        {tableChrome}
        <div className="bb-mt-empty">
          <span className="bb-mt-empty-text">
            No rows match the current filters.
          </span>
          <button type="button" className="bb-mt-empty-link" onClick={() => reset()}>
            Reset filters
          </button>
        </div>
      </div>
    );
  }

  /* ── Header ─────────────────────────────────────────────────────────── */

  // AGENT-DASH-PARITY: mode-driven column emphasis. PBI's bookmarks swap
  // which set of count/quantity columns the user looks at — Quotes mode
  // = Issued/Quoted, Orders mode = Converted/Won/Sold. We mirror that by
  // bolding the matching columns when the mode flips.
  function isEmphasisedColumn(name: string): boolean {
    if (mode === "quotes") return RX_QUOTES_COLS.test(name);
    if (mode === "orders") return RX_ORDERS_COLS.test(name);
    return false;
  }

  const flatHeader = (
    <thead className="bb-mt-thead">
      <tr className="bb-mt-thead-r">
        {columns.map((col, idx) => {
          const isRowHeader = idx < rowHeaderCount;
          const isNumeric = isNumericDType(col.dtype);
          const isSorted = sort && sort.column === col.name;
          const arrow = isSorted ? (sort!.dir === "asc" ? " ↑" : " ↓") : "";
          const emphasis = !isRowHeader && isEmphasisedColumn(col.name);
          // AGENT-DASH-LABELS: humanize the raw DAX/queryRef column name.
          // Sort still keys on `col.name` (the raw token) so changing the
          // display label can't break the existing sort/compare path.
          const displayName = humanizeColumnName(col.name);
          const classes = [
            "bb-mt-th",
            isRowHeader ? "bb-mt-th-rowhead" : "bb-mt-th-measure",
            isRowHeader ? "bb-mt-sticky-left" : "",
            isNumeric ? "bb-mt-align-right" : "bb-mt-align-left",
            emphasis ? "bb-mt-th--emphasis" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <th
              key={col.name}
              scope="col"
              className={classes}
              onClick={() => onHeaderClick(col)}
              title={headerTitle(col.name, displayName)}
              style={isRowHeader ? { left: stickyLeftFor(idx, columns) } : undefined}
            >
              {displayName}
              {arrow}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  /* ── AGENT-DASH-TREE: shared measure-cell renderer (used by grouped rows).
   * Mirrors the conditional-formatting logic in the flat renderRow below so
   * the tree view keeps the same data-bars / heatmap / mode emphasis. */
  const renderMeasureTd = (
    col: PivotColumn,
    value: PivotCellValue,
  ): JSX.Element => {
    const isNumeric = isNumericDType(col.dtype);
    const displayName = humanizeColumnName(col.name);
    const display = isYearColumn(col.name, displayName)
      ? formatYear(value)
      : isDateLike(col.name, displayName, col.dtype)
        ? formatDateMMDDYYYY(value)
        : formatValue(col.name, value, col.dtype);
    const emphasis = isEmphasisedColumn(col.name);
    const cf = colCondFmt.get(col.name);
    const numeric = isNumeric ? coerceNumber(value) : null;

    let cfClass = "";
    let cfInlineStyle: CSSProperties | undefined;
    let barWrapperClass = "";
    let barWrapperStyle: CSSProperties | undefined;

    if (cf && cf.kind === "databar" && numeric !== null) {
      cfClass = "bb-mt-td--databar";
      if (cf.centered) {
        const denom = cf.stats.absMax || 1;
        const pct = Math.min(50, (Math.abs(numeric) / denom) * 50);
        barWrapperClass =
          "bb-mt-databar " +
          (numeric >= 0
            ? "bb-mt-databar--centered-pos"
            : "bb-mt-databar--centered-neg");
        barWrapperStyle = { ["--cf-bar-pct" as string]: `${pct}%` };
      } else {
        const denom = cf.stats.max || 1;
        const pct = Math.max(0, Math.min(100, (numeric / denom) * 100));
        barWrapperClass = "bb-mt-databar";
        barWrapperStyle = { ["--cf-bar-pct" as string]: `${pct}%` };
      }
    } else if (cf && cf.kind === "heatmap" && numeric !== null) {
      cfClass = "bb-mt-td--heatmap";
      cfInlineStyle = {
        backgroundColor: heatmapColor(numeric, cf.stats.min, cf.stats.max, heatStops),
        color: heatTextColor,
      };
    } else if (cf && cf.kind === "yoyqoq" && numeric !== null) {
      cfClass = "bb-mt-td--yoyqoq";
      cfInlineStyle = {
        backgroundColor: yoyQoqColor(numeric, heatStops),
        color: heatTextColor,
      };
    }

    const classes = [
      "bb-mt-td",
      "bb-mt-td-measure",
      isNumeric ? "bb-mt-align-right dash-num" : "bb-mt-align-left",
      emphasis ? "bb-mt-td--emphasis" : "",
      cfClass,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <td
        key={col.name}
        className={classes}
        style={cfInlineStyle}
        data-column={col.name}
      >
        {barWrapperClass ? (
          <span className={barWrapperClass} style={barWrapperStyle}>
            {display}
          </span>
        ) : (
          display
        )}
      </td>
    );
  };

  /* ── AGENT-DASH-TREE: grouped header — one tree column (the group dim) +
   * the measure columns. The folded period column(s) are not shown as their
   * own header; they appear inline in the expanded child rows. */
  const groupedHeader = groupCfg ? (
    <thead className="bb-mt-thead">
      <tr className="bb-mt-thead-r">
        <th
          scope="col"
          className="bb-mt-th bb-mt-th-rowhead bb-mt-sticky-left bb-mt-align-left bb-mt-th--tree"
          style={{ left: 0 }}
          onClick={() => onHeaderClick(groupCfg.groupCol)}
          title={`Sort by ${humanizeColumnName(groupCfg.groupCol.name)}`}
        >
          {humanizeColumnName(groupCfg.groupCol.name)}
          {sort && sort.column === groupCfg.groupCol.name
            ? sort.dir === "asc"
              ? " ↑"
              : " ↓"
            : ""}
        </th>
        {groupCfg.measureCols.map((col) => {
          const isNumeric = isNumericDType(col.dtype);
          const isSorted = sort && sort.column === col.name;
          const arrow = isSorted ? (sort!.dir === "asc" ? " ↑" : " ↓") : "";
          const emphasis = isEmphasisedColumn(col.name);
          const displayName = humanizeColumnName(col.name);
          const classes = [
            "bb-mt-th",
            "bb-mt-th-measure",
            isNumeric ? "bb-mt-align-right" : "bb-mt-align-left",
            emphasis ? "bb-mt-th--emphasis" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <th
              key={col.name}
              scope="col"
              className={classes}
              onClick={() => onHeaderClick(col)}
              title={headerTitle(col.name, displayName)}
            >
              {displayName}
              {arrow}
            </th>
          );
        })}
      </tr>
    </thead>
  ) : null;

  /* ── Row renderer ──────────────────────────────────────────────────── */

  const renderRow = (row: PivotRow, key: string | number): JSX.Element => (
    <tr key={key} className="bb-mt-tr">
      {columns.map((col, idx) => {
        const isRowHeader = idx < rowHeaderCount;
        const isNumeric = isNumericDType(col.dtype);
        const value = row[col.name] ?? null;
        // AGENT-DASH-LABELS: date columns get MM/DD/YYYY (with em-dash for
        // null/NaT/epoch sentinels). Everything else still flows through
        // the existing currency / percent / count / generic formatter —
        // we are only adding date support here, not replacing the others.
        //
        // Year guard takes priority over the date branch: the raw column
        // name "Date Year" / "Calendar Year" matches RAW_DATE_RX inside
        // isDateLike, but the integer value (e.g. 2026) is not a date and
        // must render as a plain integer (em-dash for null/0/out-of-range).
        const displayName = humanizeColumnName(col.name);
        const display = isYearColumn(col.name, displayName)
          ? formatYear(value)
          : isDateLike(col.name, displayName, col.dtype)
            ? formatDateMMDDYYYY(value)
            : formatValue(col.name, value, col.dtype);
        const emphasis = !isRowHeader && isEmphasisedColumn(col.name);

        /* AGENT-B6: conditional formatting.
         *
         * Pull the column's pre-computed classification + min/max. Skip
         * for row headers and non-numeric cells. We compute `barPct` /
         * `heatBg` per cell but the gradient and color stops themselves
         * live in MetricsTable.condfmt.css. */
        const cf = !isRowHeader ? colCondFmt.get(col.name) : undefined;
        const numeric = isNumeric ? coerceNumber(value) : null;

        let cfClass = "";
        let cfInlineStyle: CSSProperties | undefined;
        let barWrapperClass = "";
        let barWrapperStyle: CSSProperties | undefined;

        if (cf && cf.kind === "databar" && numeric !== null) {
          cfClass = "bb-mt-td--databar";
          if (cf.centered) {
            // Bar grows out from the 50% axis. `pct` is half-width so
            // 100% absMax → full 50% extension to one side.
            const denom = cf.stats.absMax || 1;
            const pct = Math.min(50, (Math.abs(numeric) / denom) * 50);
            barWrapperClass =
              "bb-mt-databar " +
              (numeric >= 0
                ? "bb-mt-databar--centered-pos"
                : "bb-mt-databar--centered-neg");
            barWrapperStyle = { ["--cf-bar-pct" as string]: `${pct}%` };
          } else {
            // Left-anchored bar: 0% → 100% across the cell.
            const denom = cf.stats.max || 1;
            const pct = Math.max(
              0,
              Math.min(100, (numeric / denom) * 100),
            );
            barWrapperClass = "bb-mt-databar";
            barWrapperStyle = { ["--cf-bar-pct" as string]: `${pct}%` };
          }
        } else if (cf && cf.kind === "heatmap" && numeric !== null) {
          cfClass = "bb-mt-td--heatmap";
          cfInlineStyle = {
            backgroundColor: heatmapColor(numeric, cf.stats.min, cf.stats.max, heatStops),
            color: heatTextColor,
          };
        } else if (cf && cf.kind === "yoyqoq" && numeric !== null) {
          cfClass = "bb-mt-td--yoyqoq";
          cfInlineStyle = {
            backgroundColor: yoyQoqColor(numeric, heatStops),
            color: heatTextColor,
          };
        }

        const classes = [
          "bb-mt-td",
          isRowHeader ? "bb-mt-td-rowhead" : "bb-mt-td-measure",
          isRowHeader ? "bb-mt-sticky-left" : "",
          isNumeric ? "bb-mt-align-right dash-num" : "bb-mt-align-left",
          emphasis ? "bb-mt-td--emphasis" : "",
          cfClass,
        ]
          .filter(Boolean)
          .join(" ");

        // Sticky-left cells override `style`; combine with cf inline
        // style if both apply (only heatmap uses inline style, and
        // sticky-left columns are string-typed row headers, so in
        // practice this never collides — but be explicit).
        const cellStyle: CSSProperties | undefined = isRowHeader
          ? { left: stickyLeftFor(idx, columns) }
          : cfInlineStyle;

        return (
          <td
            key={col.name}
            className={classes}
            style={cellStyle}
            data-column={col.name}
          >
            {barWrapperClass ? (
              <span className={barWrapperClass} style={barWrapperStyle}>
                {display}
              </span>
            ) : (
              display
            )}
          </td>
        );
      })}
    </tr>
  );

  /* ── AGENT-DASH-TREE: grouped display-row renderer ──────────────────── */

  const renderDisplayRow = (dr: DisplayRow, key: string | number): JSX.Element => {
    if (!groupCfg) return <tr key={key} />;
    if (dr.kind === "group") {
      const node = dr.node;
      const expanded = expandedGroups.has(node.name);
      return (
        <tr
          key={key}
          className="bb-mt-tr bb-mt-tr--group"
          onClick={() => toggleGroup(node.name)}
        >
          <td
            className="bb-mt-td bb-mt-td-rowhead bb-mt-sticky-left bb-mt-align-left bb-mt-td--tree"
            style={{ left: 0 }}
          >
            <span
              className={`bb-mt-tree-caret${expanded ? " is-open" : ""}`}
              aria-hidden
            >
              ▸
            </span>
            <span className="bb-mt-tree-name" title={node.name}>
              {node.name}
            </span>
          </td>
          {groupCfg.measureCols.map((col) =>
            renderMeasureTd(col, node.agg[col.name] ?? null),
          )}
        </tr>
      );
    }
    const row = dr.row;
    const childLabel = groupCfg.childCols
      .map((c) => {
        const v = row[c.name] ?? null;
        const dn = humanizeColumnName(c.name);
        return isYearColumn(c.name, dn)
          ? formatYear(v)
          : isDateLike(c.name, dn, c.dtype)
            ? formatDateMMDDYYYY(v)
            : formatValue(c.name, v, c.dtype);
      })
      .join(" / ");
    return (
      <tr key={key} className="bb-mt-tr bb-mt-tr--child">
        <td
          className="bb-mt-td bb-mt-td-rowhead bb-mt-sticky-left bb-mt-align-left bb-mt-td--tree bb-mt-td--child"
          style={{ left: 0 }}
        >
          <span className="bb-mt-tree-childlabel">{childLabel}</span>
        </td>
        {groupCfg.measureCols.map((col) =>
          renderMeasureTd(col, row[col.name] ?? null),
        )}
      </tr>
    );
  };

  /* ── Body — virtualized or plain (flat or grouped) ──────────────────── */

  const colSpan = groupCfg ? groupCfg.measureCols.length + 1 : columns.length;

  function virtualSpacers(): { top: number; bottom: number } {
    const items = virtualizer.getVirtualItems();
    return {
      top: items[0]?.start ?? 0,
      bottom: Math.max(0, virtualizer.getTotalSize() - (items.at(-1)?.end ?? 0)),
    };
  }

  function buildBody(): JSX.Element {
    const renderAt = (index: number, key: string): JSX.Element =>
      groupCfg
        ? renderDisplayRow(displayRows[index], key)
        : renderRow(sortedRows[index], key);
    if (shouldVirtualize) {
      const { top, bottom } = virtualSpacers();
      return (
        <tbody className="bb-mt-tbody" style={{ position: "relative" }}>
          <tr aria-hidden="true" style={{ height: top }}>
            <td colSpan={colSpan} style={{ padding: 0, border: 0 }} />
          </tr>
          {virtualizer.getVirtualItems().map((vi) => renderAt(vi.index, String(vi.key)))}
          <tr aria-hidden="true" style={{ height: bottom }}>
            <td colSpan={colSpan} style={{ padding: 0, border: 0 }} />
          </tr>
        </tbody>
      );
    }
    return (
      <tbody className="bb-mt-tbody">
        {groupCfg
          ? displayRows.map((_, i) => renderAt(i, String(i)))
          : sortedRows.map((_, i) => renderAt(i, String(i)))}
      </tbody>
    );
  }

  const header = groupCfg ? groupedHeader : flatHeader;
  const body = buildBody();

  return (
    <div className="dash-card bb-mt-card">
      {tableChrome}
      <div
        className="bb-mt-scroll"
        ref={scrollRef}
        style={{
          // Cap the height (internal scroll) when virtualizing OR grouped, so
          // the grouped tree stays compact instead of running down the page.
          maxHeight:
            shouldVirtualize || groupCfg ? `${ROW_HEIGHT_PX * 16}px` : undefined,
          overflow: shouldVirtualize || groupCfg ? "auto" : undefined,
        }}
      >
        <table className="bb-mt-table">
          {header}
          {body}
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Sticky-left offset for the Nth row-header column. Each row-header column
 * gets a `min-width: 160px` in CSS, but we let it size to content and place
 * subsequent sticky columns at a cumulative left offset using a fixed step.
 * This keeps the sticky behavior predictable without measuring DOM widths.
 * ------------------------------------------------------------------------- */

const STICKY_COL_WIDTH_PX = 180;

function stickyLeftFor(idx: number, _columns: PivotColumn[]): number {
  return idx * STICKY_COL_WIDTH_PX;
}

export default MetricsTable;
